import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { IMPORT_MAX_FILE_SIZE_BYTES, PHOTO_BUNDLE_MAX_FILE_SIZE_BYTES } from '@ooh/contracts';
import {
  evaluateSingleFileSelection,
  isPhotoFile,
  isZipFile,
  validatePhotoBatchSelection,
} from '../lib/upload-file-selection';

/** Raw selection state + derived mode flags, shared by both file-type flows in the upload
 * phase. Split out of `useUploadSelection` to stay under the max-lines-per-function budget. */
function useSelectionState() {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [selectedPhotoFiles, setSelectedPhotoFiles] = React.useState<File[]>([]);
  const [clientError, setClientError] = React.useState<string | null>(null);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = React.useState<number | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const isZip = selectedFile !== null && isZipFile(selectedFile);
  const isPhotoBatch = selectedPhotoFiles.length > 0;

  return {
    selectedFile,
    setSelectedFile,
    selectedPhotoFiles,
    setSelectedPhotoFiles,
    clientError,
    setClientError,
    serverError,
    setServerError,
    isDragging,
    setIsDragging,
    selectedSupplierId,
    setSelectedSupplierId,
    fileInputRef,
    isZip,
    isPhotoBatch,
    isPhotoMode: isZip || isPhotoBatch,
    hasSelection: selectedFile !== null || isPhotoBatch,
  };
}

/** Owns the file-selection state machine shared by both file-type flows in the upload phase:
 * which file(s) are selected, client-side validation errors, and the derived
 * ZIP / direct-JPG-batch / spreadsheet mode flags the JSX branches on. */
export function useUploadSelection() {
  const { t } = useTranslation('mediaImport');
  const s = useSelectionState();

  function clearSelectionErrors() {
    s.setClientError(null);
    s.setServerError(null);
    s.setSelectedSupplierId(null);
  }

  function handleFileSelect(file: File) {
    clearSelectionErrors();
    s.setSelectedPhotoFiles([]);
    const result = evaluateSingleFileSelection(file, {
      zipMaxBytes: PHOTO_BUNDLE_MAX_FILE_SIZE_BYTES,
      spreadsheetMaxBytes: IMPORT_MAX_FILE_SIZE_BYTES,
    });
    if (!result.ok) {
      s.setClientError(t(result.errorKey));
      return;
    }
    s.setSelectedFile(result.file);
  }

  /** Entry point for both the file picker and drag-drop. Routes a single non-photo file
   * through the existing zip/spreadsheet logic; routes one-or-more JPG/PNG files into the
   * direct-photo-selection batch mode (client-side zip built at upload time). */
  function handleFilesSelected(files: File[]) {
    const [first] = files;
    if (files.length === 0) return;
    if (files.length === 1 && first !== undefined && !isPhotoFile(first)) {
      handleFileSelect(first);
      return;
    }

    clearSelectionErrors();
    s.setSelectedFile(null);

    const result = validatePhotoBatchSelection(files, PHOTO_BUNDLE_MAX_FILE_SIZE_BYTES);
    if (!result.ok) {
      s.setClientError(t(result.errorKey, result.errorParams));
      s.setSelectedPhotoFiles([]);
      return;
    }
    s.setSelectedPhotoFiles(result.files);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    s.setIsDragging(false);
    handleFilesSelected(Array.from(e.dataTransfer.files));
    // Keep the hidden file input in sync with the drop path so a later picker selection
    // of the SAME filename still fires onChange (CU-86ca81670).
    if (s.fileInputRef.current) s.fileInputRef.current.value = '';
  }

  return { ...s, clearSelectionErrors, handleFilesSelected, handleDrop };
}
